import { ThemeableStack, View, Text, Spinner } from '@kit/ui/src'
import { createFileRoute, Outlet, redirect, useRouter, useLocation } from '@tanstack/react-router'
import { WebLayout } from '@web/app/components'
import { useGetPermissionsMeQuery } from '@web/app/features/access/store/accessApi'
import { accessSelectors, IRouteNode } from '@web/app/features/access'
import { authSelectors } from '@web/app/features/auth/store/authSlice'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'

const EXEMPT_PATHS = ['/cuenta/perfil', '/cuenta/cambiarContrasena']

export const Route = createFileRoute('/_index')({
  beforeLoad: ({ context, location }) => {
    if (!context.isAuthenticated) {
      throw redirect({ to: '/sign-in', search: { redirect: location.href } })
    }

    const isPasswordPending = context.userProfile?.password_created === false
    const isAtPasswordPage = location.pathname === '/cuenta/cambiarContrasena'

    if (isPasswordPending && !isAtPasswordPage) {
      throw redirect({ to: '/cuenta/cambiarContrasena', replace: true })
    }
  },
  component: RouteComponent,
})

function buildAuthorizedPaths(routes: IRouteNode[]): Set<string> {
  const paths = new Set<string>()
  routes.forEach((route) => {
    if (route.to) paths.add(route.to)
    route.children?.forEach((child) => {
      if (child.to) paths.add(child.to)
    })
  })
  return paths
}

function RouteComponent() {
  const router = useRouter()
  const location = useLocation()
  const isAuthenticated = useSelector(authSelectors.isAuthenticated)
  const routes = useSelector(accessSelectors.routes)

  const { isLoading, isFetching } = useGetPermissionsMeQuery(undefined, {
    skip: !isAuthenticated,
  })

  const userProfile = useSelector(
    (state: any) => state.appApi.queries['getProfile(undefined)']?.data
  )
  const isPasswordPending = userProfile?.password_created === false
  const isAtPasswordPage = location.pathname === '/cuenta/cambiarContrasena'

  // Redirigir si la contraseña pendiente
  useEffect(() => {
    if (isPasswordPending && !isAtPasswordPage) {
      router.navigate({ to: '/cuenta/cambiarContrasena', replace: true })
    }
  }, [isPasswordPending, isAtPasswordPage, router])

  // Invalidar rutas al desloguear
  useEffect(() => {
    if (!isAuthenticated) router.invalidate()
  }, [isAuthenticated, router])

  // Redirigir rutas no autorizadas cuando los permisos ya estén listos
  useEffect(() => {
    if (isLoading || isFetching || routes.length === 0) return
    if (EXEMPT_PATHS.some((p) => location.pathname.startsWith(p))) return

    const authorizedPaths = buildAuthorizedPaths(routes)
    if (!authorizedPaths.has(location.pathname)) {
      router.navigate({ to: '/dashboard', replace: true })
    }
  }, [routes, isLoading, isFetching, location.pathname, router])

  // --- Bloqueo de render hasta tener permisos verificados ---

  // Mientras los permisos cargan, mostrar spinner sin renderizar el Outlet
  const permissionsReady = !isLoading && !isFetching && routes.length > 0
  if (!permissionsReady) {
    return (
      <WebLayout>
        <ThemeableStack f={1} ai="center" jc="center">
          <Spinner size="large" />
        </ThemeableStack>
      </WebLayout>
    )
  }

  // Rutas exentas siempre pasan
  const isExempt = EXEMPT_PATHS.some((p) => location.pathname.startsWith(p))
  const isAuthorized = isExempt || buildAuthorizedPaths(routes).has(location.pathname)

  // Si no está autorizado, no renderizar el Outlet (el useEffect redirigirá)
  if (!isAuthorized) {
    return (
      <WebLayout>
        <ThemeableStack f={1} ai="center" jc="center">
          <Spinner size="large" />
        </ThemeableStack>
      </WebLayout>
    )
  }

  if (isPasswordPending && !isAtPasswordPage) {
    return (
      <WebLayout>
        <ThemeableStack f={1} ai="center" jc="center">
          <View animation="lazy" enterStyle={{ opacity: 0 }}>
            <Text color="$color10">Redirigiendo a cambio de contraseña obligatorio...</Text>
          </View>
        </ThemeableStack>
      </WebLayout>
    )
  }

  return (
    <WebLayout bc={'yellow'}>
      <ThemeableStack padded ai={'center'}>
        <Outlet />
      </ThemeableStack>
    </WebLayout>
  )
}
